import {useEffect} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';

/**
 * Hook to block browser back navigation if there's no Back button on the page
 * @param {boolean} hasBackButton - Whether the current page has a Back button
 */
const useBlockBackNavigation = (hasBackButton) => {
  const history = useHistory();
  const location = useLocation();
  const {data, error, lastStep} = useSelector(
    (state) => state.byocOnboardingReducer || {}
  );

  useEffect(() => {
    // If Back button exists, allow normal navigation - no blocking needed
    if (hasBackButton) {
      // Return empty cleanup function for consistency
      return () => {};
    }

    const pathToStepMap = {
      '/byoc-onboarding/start': 'start',
      '/byoc-onboarding/sign-up': 'sign-up',
      '/byoc-onboarding/sign-up-success': 'sign-up-success',
      '/byoc-onboarding/menu-setup': 'menu-setup',
      '/byoc-onboarding/order-source-integration': 'order-source-integration',
      '/byoc-onboarding/payment-details': 'payment-details',
      '/byoc-onboarding/integration-status': 'integration-status'
    };

    // Define step order for onboarding
    const steps = [
      'start',
      'sign-up',
      'sign-up-success',
      'menu-setup',
      'order-source-integration',
      'payment-details',
      'integration-status'
    ];

    // Block browser back navigation
    const unblock = history.block((tx) => {
      // Check if user is trying to go back
      const currentPath = location.pathname;
      const targetPath = tx.location?.pathname || tx.pathname;
      const actionTx = tx.action;

      if (actionTx === 'PUSH' || actionTx === 'REPLACE') {
        return undefined; // Allow all programmatic navigation
      }

      // Allow navigation to other routes (not onboarding)
      if (!targetPath.startsWith('/byoc-onboarding')) {
        // Allow navigation - return undefined explicitly
        return undefined;
      }

      const targetStep = pathToStepMap[targetPath];
      const currentStep = pathToStepMap[currentPath];

      if (lastStep && targetStep === lastStep) {
        return undefined; // Allow navigation to lastStep
      }

      if (error && targetStep === 'start') {
        return undefined;
      }

      if (!data?.signup?.isFinished && targetStep === 'start') {
        return undefined;
      }
      if (!targetStep || !currentStep) {
        // Fallback: используем старую логику с includes для совместимости
        const currentStepIndex = steps.findIndex((step) =>
          currentPath.includes(step)
        );
        const targetStepIndex = steps.findIndex((step) =>
          targetPath.includes(step)
        );

        if (targetStepIndex >= 0 && targetStepIndex < currentStepIndex) {
          return false;
        }
        return undefined;
      }

      const currentStepIndex = steps.findIndex((step) =>
        currentPath.includes(step)
      );
      const targetStepIndex = steps.findIndex((step) =>
        targetPath.includes(step)
      );

      // Allow transition from integration-status to payment-details if Stripe flow is not completed
      const isFromIntegrationStatus = currentPath.includes(
        'integration-status'
      );
      const isToPaymentDetails = targetPath.includes('payment-details');
      const paymentDetails = data?.paymentDetails || data?.PaymentDetails || {};
      const isPaymentMethodNotConnected =
        paymentDetails?.paymentMethodConnected === false ||
        paymentDetails?.paymentMethodConnected === undefined;
      const orderSourceData =
        data?.orderSourceIntegration || data?.OrderSourceIntegration || {};
      const isOrderSourceFinished =
        orderSourceData?.isFinished === true ||
        orderSourceData?.IsFinished === true;

      // Allow navigation to payment-details from integration-status if payment method is not connected
      if (
        isFromIntegrationStatus &&
        isToPaymentDetails &&
        isOrderSourceFinished &&
        isPaymentMethodNotConnected
      ) {
        // Allow navigation - return undefined explicitly
        return undefined;
      }

      // Block if trying to go to a previous step
      if (targetStepIndex >= 0 && targetStepIndex < currentStepIndex) {
        // Block navigation - return false to block
        return false;
      }

      // Allow forward navigation or same step - return undefined explicitly
      return undefined;
    });

    // Cleanup function to unblock navigation
    return () => {
      if (unblock) {
        unblock();
      }
    };
  }, [hasBackButton, history, location, data, error, lastStep]);
};

export default useBlockBackNavigation;
